import { Star } from "lucide-react";
import { useDanceStyles } from "@/hooks/useDanceStyles";
import { MAX_MAIN_STYLES } from "convex/danceStyleDefaults";
import { cn } from "@/lib/utils";

interface DanceStyleSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  /**
   * The event's main styles. Pass both of these to let the organizer star up
   * to three picked styles to lead the event's badges and share tags; venue
   * forms leave them out and get the plain picker.
   */
  mainStyles?: string[];
  onMainStylesChange?: (value: string[]) => void;
}

export function DanceStyleSelect({
  value,
  onChange,
  mainStyles = [],
  onMainStylesChange,
}: DanceStyleSelectProps) {
  const { styles, groups, styleMap, orderStyles } = useDanceStyles();

  const toggleStyle = (slug: string) => {
    if (value.includes(slug)) {
      onChange(value.filter((s) => s !== slug));
      // A style that is no longer picked can't be a main one.
      if (mainStyles.includes(slug)) onMainStylesChange?.(mainStyles.filter((s) => s !== slug));
    } else {
      onChange([...value, slug]);
    }
  };

  const toggleMain = (slug: string) => {
    if (!onMainStylesChange) return;
    if (mainStyles.includes(slug)) {
      onMainStylesChange(mainStyles.filter((s) => s !== slug));
    } else if (mainStyles.length < MAX_MAIN_STYLES) {
      onMainStylesChange([...mainStyles, slug]);
    }
  };

  const picked = orderStyles(value, mainStyles).filter((slug) => styleMap[slug]);

  return (
    <div className="space-y-4">
      {groups.map((group) => {
        const groupStyles = styles.filter((s) => s.group === group);
        if (groupStyles.length === 0) return null;
        return (
          <div key={group}>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              {group}
            </p>
            <div className="flex flex-wrap gap-2">
              {groupStyles.map((style) => {
                const isSelected = value.includes(style.slug);
                return (
                  <button
                    key={style.slug}
                    type="button"
                    onClick={() => toggleStyle(style.slug)}
                    className={cn(
                      "px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200",
                      "outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                      isSelected
                        ? "text-white shadow-md scale-105"
                        : "bg-card border border-border hover:border-transparent"
                    )}
                    style={
                      isSelected
                        ? {
                            backgroundColor: style.color,
                            boxShadow: `0 4px 12px ${style.color}40`,
                          }
                        : {
                            color: style.color,
                          }
                    }
                  >
                    {style.name}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {onMainStylesChange && picked.length > 1 && (
        <div className="rounded-lg border border-border bg-secondary/30 px-3 py-3">
          <p className="text-sm font-medium text-foreground">Main styles</p>
          <p className="text-xs text-muted-foreground mt-0.5 mb-2">
            Star up to {MAX_MAIN_STYLES} to lead this event's tags, share previews and colour.
            The rest follow in the site's usual order.
          </p>
          <div className="flex flex-wrap gap-2">
            {picked.map((slug) => {
              const style = styleMap[slug]!;
              const isMain = mainStyles.includes(slug);
              const full = !isMain && mainStyles.length >= MAX_MAIN_STYLES;
              return (
                <button
                  key={slug}
                  type="button"
                  onClick={() => toggleMain(slug)}
                  disabled={full}
                  aria-pressed={isMain}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold",
                    "border transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isMain
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border bg-card text-muted-foreground hover:text-foreground",
                    full && "opacity-40 cursor-not-allowed"
                  )}
                >
                  <Star
                    className={cn("w-3.5 h-3.5", isMain ? "fill-primary text-primary" : "")}
                  />
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: style.color }}
                    aria-hidden
                  />
                  {style.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
